import type { SystemLog } from '@wf-coe/types';
import {
  Button,
  Label,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  TextInput,
} from 'flowbite-react';
import { useEffect, useMemo, useState } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import { useLicensesQuery } from '../../queries/use-licenses.js';
import { type SystemLogsQuery, useSystemLogsQuery } from '../../queries/use-system-logs.js';

/**
 * `/clients/:id/system-logs` — engine system-log viewer for a tenant.
 *
 * Mirrors the legacy `wf-adminconsole/components/dashboard/logs/SystemLogsModal.tsx`
 * UX (Type + Process + From/To filters, table of rows) but rendered as a
 * full sub-tab instead of a modal.
 *
 * A client may own multiple licenses (pro + enterprise + per-customer
 * variants), and engine logs are partition-keyed on `licenseId`, so the
 * page starts with a license picker. The first row is selected by
 * default; an Enabled badge is shown so operators can tell at a glance
 * which license actually has live data.
 *
 * Filters:
 *   - `from` / `to` are ISO 8601 timestamps. The engine defaults to "last
 *     24 hours" when both are blank — so we leave them blank initially
 *     and only send them on the wire when the operator picks a value.
 *   - `type` is a free-form filter (Error / Warning / Info) and is sent
 *     to the engine verbatim.
 *   - `process` is matched server-side (exact match) per the engine
 *     query contract.
 *
 * The engine returns `{ data: SystemLog[] }`; `useSystemLogsQuery`
 * unwraps the envelope so this component sees a flat `SystemLog[]`.
 */
export function SystemLogs(): JSX.Element {
  const { id: clientId } = useParams<{ id: string }>();
  const licensesQuery = useLicensesQuery(clientId);

  const licenses = useMemo(() => licensesQuery.data ?? [], [licensesQuery.data]);
  const [licenseId, setLicenseId] = useState<string | undefined>(undefined);

  // Auto-pick the first enabled license (or the first license overall)
  // once licenses load, but only if the operator hasn't picked one yet.
  // Re-running on every licenses change would clobber an explicit pick.
  useEffect(() => {
    if (licenseId) return;
    const first = licenses.find((l) => l.enabled) ?? licenses[0];
    if (first) setLicenseId(first.id);
  }, [licenses, licenseId]);

  // Filter form state — kept separate from the query filter so the
  // operator can fiddle with from/to/type/process without re-firing
  // the request on every keystroke. Pressing "Refresh" (or changing
  // the license) flushes the form into the active filter.
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');
  const [typeInput, setTypeInput] = useState('');
  const [processInput, setProcessInput] = useState('');
  // Row-count cap. Default 30 — what an operator usually wants on
  // page load (latest pipeline activity). The selector lets them
  // bump it to 100/500/1000 without leaving the page. Applied on
  // the engine side via `?limit=`, which uses DDB's `Limit`
  // parameter so we don't pay for streaming the whole 24h window.
  const [limit, setLimit] = useState<number>(30);
  const [filter, setFilter] = useState<SystemLogsQuery>({ limit: 30 });

  // Auto-refresh cadence (Grafana-style). 0 = off; otherwise the
  // millisecond interval. TanStack Query handles the timer + cleanup
  // and pauses when the tab is hidden, so leaving "5s" on doesn't keep
  // polling the engine after the operator switches away.
  const [refreshMs, setRefreshMs] = useState(0);

  const logsQuery = useSystemLogsQuery(licenseId, filter, {
    refetchIntervalMs: refreshMs > 0 ? refreshMs : false,
  });

  // Engine returns newest-first (`ScanIndexForward: false`) — no
  // client-side sort needed. We pass through the array as-is.
  const sortedRows = logsQuery.data;

  const applyFilters = (limitOverride?: number): void => {
    setFilter({
      from: fromInput ? toIsoFromLocalDate(fromInput) : undefined,
      to: toInput ? toIsoFromLocalDate(toInput, /* endOfDay */ true) : undefined,
      type: typeInput || undefined,
      process: processInput.trim() || undefined,
      limit: limitOverride ?? limit,
    });
  };

  const resetFilters = (): void => {
    setFromInput('');
    setToInput('');
    setTypeInput('');
    setProcessInput('');
    setLimit(30);
    setFilter({ limit: 30 });
  };

  // Bumping the Show-N selector re-fires the query immediately —
  // no separate Apply click. The other filters keep their click-
  // to-apply UX so the operator can compose a multi-field query
  // without intermediate refetches.
  const handleLimitChange = (next: number): void => {
    setLimit(next);
    setFilter((prev) => ({ ...prev, limit: next }));
  };

  return (
    <section>
      <header className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">System logs</h2>
        <div className="flex items-center gap-2">
          <Label htmlFor="sl-limit" className="text-xs text-gray-500 dark:text-gray-400">
            Show
          </Label>
          <Select
            id="sl-limit"
            sizing="sm"
            value={String(limit)}
            onChange={(e) => handleLimitChange(Number.parseInt(e.target.value, 10))}
            disabled={!licenseId}
          >
            {LIMIT_OPTIONS.map((opt) => (
              <option key={opt.value} value={String(opt.value)}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Label htmlFor="sl-auto-refresh" className="text-xs text-gray-500 dark:text-gray-400">
            Auto-refresh
          </Label>
          <Select
            id="sl-auto-refresh"
            sizing="sm"
            value={String(refreshMs)}
            onChange={(e) => setRefreshMs(Number.parseInt(e.target.value, 10))}
            disabled={!licenseId}
          >
            {AUTO_REFRESH_OPTIONS.map((opt) => (
              <option key={opt.ms} value={String(opt.ms)}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Button
            size="sm"
            color="gray"
            onClick={() => void logsQuery.refetch()}
            disabled={!licenseId || logsQuery.isFetching}
          >
            <FiRefreshCw className={`mr-1 ${logsQuery.isFetching ? 'animate-spin' : ''}`} />{' '}
            {logsQuery.isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>
      </header>

      <div className="mb-4 grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-white p-3 sm:grid-cols-2 lg:grid-cols-5 dark:border-gray-700 dark:bg-gray-800">
        <div className="lg:col-span-2">
          <Label htmlFor="sl-license">License</Label>
          {licensesQuery.isLoading ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" role="status">
              Loading licenses…
            </p>
          ) : licenses.length === 0 ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              This client has no licenses yet.
            </p>
          ) : (
            <Select
              id="sl-license"
              value={licenseId ?? ''}
              onChange={(e) => setLicenseId(e.target.value || undefined)}
            >
              {licenses.map((l) => (
                <option key={l.id} value={l.id}>
                  {(l.type ?? 'license').toUpperCase()} · {l.id.slice(0, 8)}…{' '}
                  {l.enabled ? '· enabled' : '· disabled'}
                </option>
              ))}
            </Select>
          )}
        </div>
        <div>
          <Label htmlFor="sl-from">From</Label>
          <TextInput
            id="sl-from"
            type="date"
            value={fromInput}
            onChange={(e) => setFromInput(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="sl-to">To</Label>
          <TextInput
            id="sl-to"
            type="date"
            value={toInput}
            onChange={(e) => setToInput(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="sl-type">Type</Label>
          <Select id="sl-type" value={typeInput} onChange={(e) => setTypeInput(e.target.value)}>
            <option value="">All</option>
            <option value="Error">Error</option>
            <option value="Warning">Warning</option>
            <option value="Info">Info</option>
          </Select>
        </div>
        <div className="sm:col-span-2 lg:col-span-4">
          <Label htmlFor="sl-process">Process</Label>
          <TextInput
            id="sl-process"
            placeholder="exact match — e.g. main-queue, fusion-metrics-collection"
            value={processInput}
            onChange={(e) => setProcessInput(e.target.value)}
          />
        </div>
        <div className="flex items-end justify-end gap-2">
          <Button size="sm" color="gray" onClick={resetFilters}>
            Reset
          </Button>
          <Button size="sm" onClick={() => applyFilters()} disabled={!licenseId}>
            Apply
          </Button>
        </div>
      </div>

      {!licenseId ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Pick a license to view its logs.</p>
      ) : logsQuery.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="xl" aria-label="Loading system logs" />
        </div>
      ) : logsQuery.isError ? (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
        >
          {logsQuery.error instanceof Error
            ? logsQuery.error.message
            : 'Failed to load system logs'}
        </div>
      ) : !sortedRows || sortedRows.length === 0 ? (
        <p className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          No logs in the selected window.
        </p>
      ) : (
        <LogsTable rows={sortedRows} />
      )}
    </section>
  );
}

/**
 * Auto-refresh cadence options, Grafana-style. 0 ms = off.
 *
 * The set is deliberately short — operators either want "show me
 * what just happened" (5s / 10s during a triggered pipeline) or
 * "ambient background polling" (1m / 5m). Anything finer than 5s
 * floods the engine; anything coarser than 5m is a manual refresh.
 */
const AUTO_REFRESH_OPTIONS: readonly { label: string; ms: number }[] = [
  { label: 'Off', ms: 0 },
  { label: '5s', ms: 5_000 },
  { label: '10s', ms: 10_000 },
  { label: '30s', ms: 30_000 },
  { label: '1m', ms: 60_000 },
  { label: '5m', ms: 300_000 },
];

/**
 * Row-count cap, applied via `?limit=` on the engine query. Default
 * is 30 — what an operator usually wants when popping the page open
 * to see what the latest pipeline run did. Larger windows are
 * available for triage on a noisy day; the engine caps anything
 * over 1000 at 1000.
 */
const LIMIT_OPTIONS: readonly { label: string; value: number }[] = [
  { label: 'Last 30', value: 30 },
  { label: 'Last 100', value: 100 },
  { label: 'Last 500', value: 500 },
  { label: 'Last 1000', value: 1000 },
];

/**
 * Tabular renderer. Matches legacy columns: Type / Log Time / Process /
 * Message / Info / Scenario id. Type cell is color-coded
 * (red for errors, amber for warnings, gray for info) like the legacy
 * modal so an operator's eye lands on the failures first.
 *
 * `infoObj` is rendered as pretty-printed JSON inside a `<pre>`. The
 * legacy modal pulled in `react18-json-view` for a collapsible tree;
 * keeping the dep surface lean here — operators routinely copy this
 * cell into other tools, where flat JSON is more useful anyway.
 */
function LogsTable({ rows }: { rows: SystemLog[] }): JSX.Element {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeadCell>Type</TableHeadCell>
            <TableHeadCell>Log time</TableHeadCell>
            <TableHeadCell>Process</TableHeadCell>
            <TableHeadCell>Message</TableHeadCell>
            <TableHeadCell>Info</TableHeadCell>
            <TableHeadCell>Scenario id</TableHeadCell>
          </TableRow>
        </TableHead>
        <TableBody className="divide-y divide-gray-200 dark:divide-gray-700">
          {rows.map((log, index) => (
            <TableRow
              key={`${log.logTime}-${index}`}
              className="bg-white align-top dark:bg-gray-800"
            >
              <TableCell className={typeClass(log.type)}>{log.type}</TableCell>
              <TableCell className="whitespace-nowrap font-mono text-xs">
                {formatLogTime(log.logTime)}
              </TableCell>
              <TableCell className="whitespace-nowrap text-xs">{log.process}</TableCell>
              <TableCell>
                <p className={`whitespace-pre-wrap text-sm ${typeClass(log.type)}`}>
                  {log.message}
                </p>
                {log.infoText ? (
                  <p className="mt-1 whitespace-pre-wrap text-xs text-gray-500 dark:text-gray-400">
                    {log.infoText}
                  </p>
                ) : null}
              </TableCell>
              <TableCell>
                {log.infoObj === null || log.infoObj === undefined ? (
                  <span className="text-gray-400 dark:text-gray-500">—</span>
                ) : (
                  <pre className="max-h-48 max-w-md overflow-auto rounded bg-gray-50 p-2 text-xs text-gray-800 dark:bg-gray-900 dark:text-gray-200">
                    {prettyJson(log.infoObj)}
                  </pre>
                )}
              </TableCell>
              <TableCell className="whitespace-nowrap font-mono text-xs">
                {log.fusionScenarioId ?? '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function typeClass(type: string): string {
  const t = type.toLowerCase();
  if (t === 'error') return 'font-semibold text-red-600 dark:text-red-400';
  if (t === 'warning') return 'font-semibold text-amber-600 dark:text-amber-400';
  return 'text-gray-700 dark:text-gray-300';
}

function formatLogTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // YYYY-MM-DD HH:mm:ss local — easier to scan than the default
  // toLocaleString() output, which varies by browser locale.
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

function prettyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/**
 * Convert a `yyyy-MM-dd` date input value to an ISO 8601 timestamp.
 * Without an explicit time, treat "from" as start-of-day and "to" as
 * end-of-day in the operator's local timezone so a single-day window
 * matches the calendar day they picked.
 */
function toIsoFromLocalDate(yyyyMmDd: string, endOfDay = false): string | undefined {
  if (!yyyyMmDd) return undefined;
  const [y, m, d] = yyyyMmDd.split('-').map((s) => Number.parseInt(s, 10));
  if (!y || !m || !d) return undefined;
  const dt = endOfDay ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d, 0, 0, 0, 0);
  if (Number.isNaN(dt.getTime())) return undefined;
  return dt.toISOString();
}
